/**
 * API client. PLAN.md 3.5, 8.
 *
 * The access token lives only in this module's memory: never localStorage,
 * never sessionStorage, so an injected script cannot lift it from storage and
 * a closed tab forgets it. The refresh token is an httpOnly cookie the page
 * cannot read at all; `refresh()` trades it for a new access token.
 */
import type { ExecSubtype, Role } from '@saledeed/shared';

export interface SessionUser {
  id: string;
  userCode: string;
  email: string;
  displayName: string;
  role: Role;
  execSubtype: ExecSubtype | null;
  status: 'invited' | 'active' | 'disabled';
}

interface SessionPayload {
  accessToken: string;
  expiresIn: number;
  user: SessionUser;
}

export interface ApiIssue { path: string; message: string; code?: string }

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: ApiIssue[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshing: Promise<SessionUser | null> | null = null;
let listener: (user: SessionUser | null) => void = () => {};

export function onSessionChange(fn: (user: SessionUser | null) => void) {
  listener = fn;
}

function adopt(payload: SessionPayload): SessionUser {
  accessToken = payload.accessToken;
  if (refreshTimer) clearTimeout(refreshTimer);
  // Refresh a minute before expiry so requests never meet an expired token.
  refreshTimer = setTimeout(() => { void refresh(); }, Math.max(10, payload.expiresIn - 60) * 1000);
  listener(payload.user);
  return payload.user;
}

export function clearSession() {
  accessToken = null;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  listener(null);
}

async function parseError(res: Response): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string; issues?: ApiIssue[]; requestId?: string } } = {};
  try { body = await res.json(); } catch { /* not JSON */ }
  const e = body.error ?? {};
  return new ApiError(res.status, e.code ?? 'http_error', e.message ?? `Request failed (${res.status})`, e.issues ?? [], e.requestId);
}

async function doRefresh(): Promise<SessionUser | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
    if (res.ok) return adopt(await res.json() as SessionPayload);
    // Another tab rotated the cookie a moment ago; the browser now holds the new one.
    if (res.status === 409 && attempt === 0) { await new Promise(r => setTimeout(r, 300)); continue; }
    clearSession();
    return null;
  }
  return null;
}

/** Single-flight: concurrent callers share one refresh request. */
export function refresh(): Promise<SessionUser | null> {
  refreshing ??= doRefresh().finally(() => { refreshing = null; });
  return refreshing;
}

export interface RequestOptions {
  body?: unknown;
  /** Raw bytes (file upload); sent as application/octet-stream. */
  raw?: BodyInit;
  signal?: AbortSignal;
}

export async function api<T>(method: string, path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;
  if (opts.raw !== undefined) {
    headers['content-type'] = 'application/octet-stream';
    body = opts.raw;
  } else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;

  const res = await fetch(path, { method, headers, credentials: 'same-origin', ...(body !== undefined ? { body } : {}), ...(opts.signal ? { signal: opts.signal } : {}) });
  if (res.status === 401 && !retried && !path.startsWith('/api/auth/')) {
    if (await refresh()) return api<T>(method, path, opts, true);
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return await res.json() as T;
}

/* ---------------------------------------------------------------- auth */

export async function login(email: string, password: string) {
  return adopt(await api<SessionPayload>('POST', '/api/auth/login', { body: { email, password } }));
}

export async function redeemSetupCode(code: string, password: string) {
  return adopt(await api<SessionPayload>('POST', '/api/auth/setup', { body: { code, password } }));
}

export async function changePassword(currentPassword: string, newPassword: string) {
  return adopt(await api<SessionPayload>('POST', '/api/auth/password', { body: { currentPassword, newPassword } }));
}

export async function logout() {
  try { await api('POST', '/api/auth/logout'); } finally { clearSession(); }
}
